import { Meta, Title } from '@solidjs/meta';
import { createAsync, useSubmission } from '@solidjs/router';
import { getV0Facilities } from '@troon/api-web-client';
import { Formspree, formspreeAction } from '@troon/forms';
import { IconCircleCheckCustom } from '@troon/icons/circle-check-custom';
import {
	Container,
	Errors,
	Grid,
	GridFive,
	GridSeven,
	Heading,
	HorizontalRule,
	InputField,
	Link,
	ArticleTile,
	Marquee,
	MediaSection,
	Page,
	Picture,
	Section,
	SelectField,
	SpringNumber,
	Button,
	createListCollection,
} from '@troon/ui';
import { Match, splitProps, Show, Suspense, Switch, createSignal } from 'solid-js';
import { NoHydration } from 'solid-js/web';
import { getConfigValue } from '../../modules/config';
import type { ComponentProps, ParentProps } from 'solid-js';

export default function ForCoursesIndex() {
	const courses = createAsync(() => getV0Facilities({ supportsAccessPoints: true }), {
		deferStream: false,
	});

	const submission = useSubmission(formAction);
	const [teeSheeOther, setTeeSheetOther] = createSignal(false);

	return (
		<Page>
			<Title>For courses: Get discovered by 450k+ golfers when you join access | Golf with Access</Title>
			<Meta
				name="description"
				content="Get discovered by 450k+ golfers. Give them a reason to choose you. Keep them coming back on autopilot."
			/>
			<Container>
				<Page>
					<NoHydration>
						<div class="flex flex-col gap-8">
							<div class="flex flex-col items-stretch gap-4 text-center text-balance">
								<p class="text-sm font-medium tracking-wider text-deprecated-brand-600 uppercase md:text-base">
									For courses
								</p>
								<Heading as="h1">Fill your tee sheet with Access.</Heading>
								<p class="text-base md:text-lg">
									Get discovered by 450k+ golfers. Give them a reason to choose you. Keep them coming back on autopilot.
								</p>
							</div>
							<div class="flex justify-center">
								<Button as={Link} href="#schedule" appearance="primary">
									Schedule a demo
								</Button>
							</div>
						</div>

						<div class="relative grid aspect-1280/832 place-items-center px-4 md:px-16 lg:px-32">
							<Picture
								src={`${getConfigValue('IMAGE_HOST')}/for-courses/hero-bg.jpg`}
								loading="eager"
								mode="crop"
								crop="center"
								sizes="(min-width: 1280px) 1280w, 97vw"
								class="absolute inset-0 w-full rounded-xl bg-center object-cover"
								alt=""
								width={1280}
								height={832}
							/>
							<Picture
								src={`${getConfigValue('IMAGE_HOST')}/for-courses/screenshot.png`}
								loading="eager"
								mode="crop"
								crop="center"
								sizes="(min-width: 1280px) 1053w, 90vw"
								class="z-10 rounded-xl bg-center object-cover"
								alt=""
								width={1053}
								height={680}
							/>
						</div>
					</NoHydration>

					<div class="flex flex-wrap justify-around gap-8">
						<Stat value={200} units="+" label="Partner courses" />
						<Stat
							value={100_000_000}
							units="+"
							label="Revenue delivered"
							formatOptions={{
								currency: 'usd',
								style: 'currency',
							}}
						/>
						<Stat value={450_000} units="+" label="Active golfers" />
					</div>
				</Page>

				<HorizontalRule />
			</Container>

			<div class="flex flex-col gap-4 text-center md:gap-8">
				<Heading as="h2" size="p" class="text-sm font-normal text-deprecated-neutral-800 uppercase">
					Trusted By Top Courses Nationwide
				</Heading>
				<Suspense>
					<Marquee
						description="Trusted by top courses nationwide"
						items={courses()?.facilities}
						outerClass="max-w-100vw pointer-events-none overflow-hidden"
						pauseOnInteraction={false}
						spacing="1rem"
						itemClass="shrink-0"
					>
						{(facility) => (
							<div class="aspect-square size-36 rounded-lg border border-deprecated-neutral bg-white p-4">
								<Picture
									src={facility.logo}
									alt={facility.name}
									width={200}
									height={200}
									sizes="8rem"
									class="w-full"
									crop="center"
									mode="contain"
									loading="lazy"
								/>
							</div>
						)}
					</Marquee>
				</Suspense>
			</div>

			<NoHydration>
				<Container>
					<HorizontalRule />
					<Page>
						<MediaSection
							src={`${getConfigValue('IMAGE_HOST')}/for-courses/network.jpg`}
							aspectRatio="square"
							alt="A search bar floating above an ocean-side golf course"
						>
							<Content title="Access Network" subtitle="Get discovered by golfers you’d never reach.">
								<p>
									450k+ Access members are actively searching for their next round. We sync with your existing tee sheet
									software — your availability stays accurate, bookings flow in automatically.
								</p>
							</Content>
						</MediaSection>

						<HorizontalRule />

						<MediaSection
							reverse
							src={`${getConfigValue('IMAGE_HOST')}/for-courses/loyalty.jpg`}
							aspectRatio="square"
							alt="tee time reservations rewarding points"
						>
							<Content title="Access Loyalty" subtitle="Turn first-timers into regulars.">
								<p>
									When golfers earn points at your course, they have a reason to return. When they earn points
									everywhere on Access, they stay in the network — and keep choosing partners like you.
								</p>
							</Content>
						</MediaSection>

						<HorizontalRule />

						<Section>
							<Content title="Access Engine" subtitle="Demand and revenue on autopilot.">
								<p class="max-w-4xl text-balance">
									Cancellations get filled. At-risk bookings get saved. Slow days get promoted. The booking engine runs
									in the background so you don’t have to.
								</p>
							</Content>

							<div class="grid grid-cols-1 gap-x-8 gap-y-16 lg:grid-cols-3">
								<Feature
									title="Waitlist Auto Fill"
									subtitle="Fill cancelled tee times"
									src={`${getConfigValue('IMAGE_HOST')}/for-courses/waitlist.png`}
									alt="Mock-up image of a phone receiving notifications for available tee times at golf courses."
								>
									<p>
										Cancellations get filled. At-risk bookings get saved. Slow days get promoted. The booking engine
										runs in the background so you don’t have to.
									</p>
								</Feature>
								<Feature
									title="Check-In Reminders"
									subtitle="Stay Ahead of Cancellations"
									src={`${getConfigValue('IMAGE_HOST')}/for-courses/check-in-reminder.png`}
									alt="Mock-up image of text message confirmations for tee times, preventing cancellations."
								>
									<p>
										Automatically identify at-risk tee times before they cancel and refill them faster — protecting
										revenue and keeping your tee sheet full
									</p>
								</Feature>
								<Feature
									title="Marketing Automation"
									subtitle="Demand generation on autopilot"
									src={`${getConfigValue('IMAGE_HOST')}/for-courses/marketing-automation.png`}
									alt="Mock-up image of an email reminding golfers of available tee times that might be relevant to them."
								>
									<p>
										Targeted campaigns reach the right golfers at the right time — whether that's filling a slow Tuesday
										morning or promoting a peak-season weekend. All powered by booking data, all running without you
										lifting a finger.
									</p>
								</Feature>
							</div>
						</Section>

						<HorizontalRule />
					</Page>
				</Container>

				<Section>
					<Container>
						<Heading as="h2" class="max-w-xl text-balance">
							Meet the course operators <span class="text-deprecated-brand">getting real results.</span>
						</Heading>
					</Container>

					<div class="w-full snap-start overflow-x-auto">
						<Container>
							<div class="flex min-w-3xl gap-4 pe-4 sm:gap-6 sm:pe-0">
								<Testimonial
									hero={courses()?.facilities.find((f) => f.slug === 'monarch-beach-golf-links')?.hero}
									avatar="eric-lohman.png"
									name="Eric Lohman"
									title="General Manager"
									course="Monarch Beach"
								>
									The change to Access has been a home run for Monarch Beach Golf Links. The booking experience is
									easier and more seamless for our guests, as well as our staff in the Golfshop. We also believe that
									through marketing and improved technology efforts we have witnessed an increase in play and guest
									satisfaction. We’re excited to see what the future entails for us and the Access channels.
								</Testimonial>

								<Testimonial
									hero={courses()?.facilities.find((f) => f.slug === 'pronghorn-at-juniper-preserve')?.hero}
									avatar="jerrel-grow.jpeg"
									name="Jerrel Grow"
									title="Director of Golf"
									course="Pronghorn at Juniper Preserve"
								>
									This team has done golfers and operators a tremendous service with the launch of Access. Finally, a
									robust rewards program—similar to those used by major hotel brands—gives golfers meaningful incentives
									while helping courses build loyalty and lasting relationships.
								</Testimonial>
							</div>
						</Container>
					</div>
				</Section>

				<Section appearance="secondary">
					<Container>
						<Section>
							<Content title="Featured In" subtitle="See what the industry is saying about Access." />

							<div class="grid grid-cols-1 gap-6 md:grid-cols-3">
								<ArticleTile
									appearance="vertical-only"
									hero={{
										src: `${getConfigValue('IMAGE_HOST')}/for-courses/press-sbj.png`,
										alt: 'Sports Business Journal logo',
									}}
									subtitle="Sports Business Journal"
									title="Golf company Troon launching new 'Access' offering"
									date="2026-03-09"
									url="https://www.sportsbusinessjournal.com/Articles/2026/03/09/golf-company-troon-launching-new-access-offering/"
								/>
								<ArticleTile
									appearance="vertical-only"
									hero={{
										src: `${getConfigValue('IMAGE_HOST')}/for-courses/press-golf-inc.png`,
										alt: 'Golf Inc. logo',
									}}
									subtitle="Golf Inc."
									title="Troon launches Access golf rewards program with booking, loyalty and subscription features"
									date="2026-03-16"
									url="https://golfincmagazine.com/content/troon-launches-access-golf-rewards-program-with-booking-loyalty-and-subscription-features/"
								/>
								<ArticleTile
									appearance="vertical-only"
									hero={{
										src: `${getConfigValue('IMAGE_HOST')}/for-courses/press-forbes.png`,
										alt: 'Forbes logo',
									}}
									subtitle="Forbes"
									title="Troon Bets Golf Is Ready For Its Own Bonvoy With New Access Platform"
									date="2026-03-20"
									url="https://www.forbes.com/sites/erikmatuszewski/2026/03/20/troon-bets-golf-is-ready-for-its-own-bonvoy-with-new-access-platform/"
								/>
							</div>
						</Section>
					</Container>
				</Section>
			</NoHydration>

			<Container>
				<Page>
					<Grid id="schedule">
						<GridFive>
							<Content title="Get Started" subtitle="Schedule a demo with our team.">
								<p>
									See how Access can drive more rounds & revenue for your course. We’ll walk you through the platform,
									answer your questions, and find the right fit for your course.
								</p>
							</Content>
						</GridFive>
						<GridSeven>
							<Section appearance="contained" class="bg-deprecated-neutral-100">
								<Switch>
									<Match when={submission.result}>
										<div class="mx-auto grid max-w-xl place-items-center gap-6 text-center">
											<IconCircleCheckCustom class="text-8xl" />
											<Heading as="h1" size="h2">
												Message received!
											</Heading>
											<p class="text-lg">
												Thanks for contacting us!
												<br />
												Our team is will get in touch with you shortly.
											</p>
										</div>
									</Match>
									<Match when>
										<Formspree action={formAction}>
											<div class="flex justify-stretch gap-4">
												<InputField name="First name" label="First name" autocomplete="given-name" />
												<InputField name="Last name" label="Last name" autocomplete="family-name" />
											</div>

											<InputField name="email" label="Email" autocomplete="email" inputMode="email" />

											<InputField name="Course name" label="Course name" autocomplete="off" />

											<SelectField
												name="Tee sheet software"
												label="Tee sheet software"
												onValueChange={(change) => {
													setTeeSheetOther(change.value[0] === 'Other');
												}}
												collection={createListCollection({
													items: [
														{ value: 'Golf365 Premier', label: 'Golf365 Premier' },
														{ value: 'EZ RTS', label: 'EZ RTS' },
														{ value: 'Lightspeed', label: 'Lightspeed' },
														{ value: 'Other', label: 'Other' },
													],
												})}
											/>

											<Show when={teeSheeOther()}>
												<InputField name="Tee sheet software (other)" label="Tee sheet software (other)" />
											</Show>

											<Errors />

											<Button appearance="primary" type="submit">
												Submit
											</Button>
										</Formspree>
									</Match>
								</Switch>
							</Section>
						</GridSeven>
					</Grid>
				</Page>
			</Container>
		</Page>
	);
}

function Stat(props: {
	value: number;
	units?: string;
	label: string;
	formatOptions?: ComponentProps<typeof SpringNumber>['formatOptions'];
}) {
	return (
		<p class="flex shrink grow basis-1/4 flex-col gap-2 text-center text-lg">
			<span class="block text-4xl font-semibold lg:text-6xl">
				<SpringNumber
					value={props.value}
					springOpts={{ stiffness: 0.05 }}
					formatOptions={{
						maximumFractionDigits: 0,
						notation: 'compact',
						compactDisplay: 'short',
						...props.formatOptions,
					}}
				/>
				{props.units}
			</span>
			<span class="text-deprecated-neutral-800 uppercase">{props.label}</span>
		</p>
	);
}

function Content(props: ParentProps & { title: string; subtitle: string }) {
	return (
		<div class="flex flex-col gap-4">
			<Heading as="h2" size="p" class="tracking-wider text-deprecated-brand-600 uppercase">
				{props.title}
			</Heading>
			<Heading as="p" size="h2">
				{props.subtitle}
			</Heading>
			{props.children}
		</div>
	);
}

function Feature(
	props: ParentProps &
		Partial<ComponentProps<typeof Picture>> & {
			title: string;
			subtitle: string;
			src: ComponentProps<typeof Picture>['src'];
		},
) {
	const [, picture] = splitProps(props, ['children', 'title', 'subtitle']);
	return (
		<div class="flex flex-col gap-4 text-sm">
			<Picture width={400} height={300} sizes="(min-width: 768px) 45vw, 95vw" {...picture} class="w-full rounded-lg" />
			<div class="flex flex-col gap-2">
				<Heading as="h2" size="p" class="text-sm font-normal tracking-wider text-deprecated-brand-600 uppercase">
					{props.title}
				</Heading>
				<Heading as="p" size="h5">
					{props.subtitle}
				</Heading>
				{props.children}
			</div>
		</div>
	);
}

function Testimonial(
	props: ParentProps<{ hero?: string | null; avatar: string; name: string; title: string; course: string }>,
) {
	return (
		<div class="relative flex h-full min-h-128 w-[90vw] shrink basis-1/2 flex-col justify-end gap-4 self-stretch overflow-hidden rounded-lg p-4 text-sm text-white sm:w-auto sm:p-6">
			<Show when={props.hero}>
				{(hero) => (
					<Picture
						src={hero()}
						alt=""
						width={400}
						height={400}
						mode="crop"
						crop="bottom"
						sizes="400w"
						loading="lazy"
						class="pointer-events-none absolute inset-0 size-full object-cover"
						draggable={false}
					/>
				)}
			</Show>
			<div class="absolute inset-0 z-10 bg-linear-to-b from-black/0 via-black/0 via-20% to-black/90 to-90%" />
			<div class="relative z-10 flex flex-col gap-4">
				<div class="font-medium">{props.children}</div>
				<div class="flex items-center gap-4">
					<Picture
						src={`${getConfigValue('IMAGE_HOST')}/for-courses/${props.avatar}`}
						alt={props.name}
						class="aspect-square size-12 shrink-0 grow-0 overflow-hidden rounded-full border border-white"
						sizes="2rem"
						width={100}
						height={100}
					/>
					<div class="flex flex-col">
						<p class="font-medium">{props.name}</p>
						<p class="text-xs">
							{props.title} • {props.course}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}

const formAction = formspreeAction(getConfigValue('FORMSPREE_PROJECT_ID'), 'course-lead-gen');
